import React from "react";
import {Typography} from "@mui/material";
import {Weekdays} from "../enums/Weekdays";
import {IdlocaGeneric} from "../locales/IdlocaGeneric";
import {Locale} from "../locales/Locale";

interface FormatWorkingHoursProps {
    data: {
        open: {
            day: number;
            hour: number;
            minute: number;
        };
        close: {
            day: number;
            hour: number;
            minute: number;
        };
    }[];
}

const weekdayTranslations = [
    {param: Weekdays.Sunday, locaId: IdlocaGeneric.sunday},
    {param: Weekdays.Monday, locaId: IdlocaGeneric.monday},
    {param: Weekdays.Tuesday, locaId: IdlocaGeneric.tuesday},
    {param: Weekdays.Wednesday, locaId: IdlocaGeneric.wednesday},
    {param: Weekdays.Thursday, locaId: IdlocaGeneric.thursday},
    {param: Weekdays.Friday, locaId: IdlocaGeneric.friday},
    {param: Weekdays.Saturday, locaId: IdlocaGeneric.saturday},
];

const formatTime = (hour: number, minute: number) => {
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
};

export const FormatWorkingHours: React.FC<FormatWorkingHoursProps> = ({data}) => {
    const hoursMap: Map<string, number[]> = new Map();

    data.forEach(({open, close}) => {
        const openTime = formatTime(open.hour, open.minute);
        const closeTime = formatTime(close.hour, close.minute);
        const timeRange = `${openTime} - ${closeTime}`;

        if (!hoursMap.has(timeRange)) {
            hoursMap.set(timeRange, []);
        }
        hoursMap.get(timeRange)!.push(open.day);
    });

    for (let i = 0; i <= 6; i++) {
        const isDayPresent = data.some(item => item.open.day === i);
        if (!isDayPresent) {
            if (!hoursMap.has("Closed")) {
                hoursMap.set("Closed", []);
            }
            hoursMap.get("Closed")!.push(i);
        }
    }

    const translateDay = (day: number) => {
        const translation = weekdayTranslations.find(item => item.param === day);
        if (translation) {
            return <Locale localeId={translation.locaId}/>;
        }
        return day;
    };

    const renderTimeRangeLabel = (timeRange: string) => {
        return timeRange === "Closed" ? <Locale localeId={IdlocaGeneric.closed}/> : timeRange;
    };

    const formattedHours: JSX.Element[] = [];
    hoursMap.forEach((days, timeRange) => {
        days.sort((a, b) => a - b);

        if (days.length === 7) {
            formattedHours.push(
                <Typography variant="caption" key={`fullweek-${timeRange}`}>
                    {translateDay(Weekdays.Monday)} - {translateDay(Weekdays.Sunday)}: {renderTimeRangeLabel(timeRange)}
                </Typography>,
            );
        }
        else {

            let start = days[0];
            let end = days[0];
            const grouped: {start: number; end: number}[] = [];

            for (let i = 1; i < days.length; i++) {
                if (days[i] === end + 1) {
                    end = days[i];
                }
                else {
                    grouped.push({start, end});
                    start = days[i];
                    end = days[i];
                }
            }
            grouped.push({start, end});

            grouped.forEach(({start, end}) => {
                if (start === end) {
                    formattedHours.push(
                        <Typography variant="caption" key={`${start}-${timeRange}`}>
                            {translateDay(start)}: {renderTimeRangeLabel(timeRange)}
                        </Typography>,
                    );
                }
                else {
                    formattedHours.push(
                        <Typography variant="caption" key={`${start}-${end}-${timeRange}`} sx={{marginRight: "3px"}}>
                            {translateDay(start)} - {translateDay(end)}: {renderTimeRangeLabel(timeRange)}
                        </Typography>,
                    );
                }
            });
        }
    });

    return (
        <div>
            {formattedHours.map((item, index) => (
                <span key={index}>{item}</span>
            ))}
        </div>
    );
};
